import {
	Dialog,
	DialogContent,
	DialogClose,
	DialogTitle,
	DialogDescription
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { X } from "lucide-react";
import { ObservationQuestionLayout } from "src/components/ObservationQuestion/ObservationQuestion";
import { FormProvider, useForm } from "react-hook-form";
import formatAnswers from "./formatAnswers";

type FormPreviewModalProps = {
	isOpen: boolean;
	onClose: () => void;
	formName: string;
	onPublish?: () => void;
	hideButtons?: boolean;
	questions?: AdminObservationQuestion[] | null;
	observationType?: IObservationType;
};

const FormPreviewModal = ({
	onPublish,
	questions,
	formName,
	hideButtons = false,
	isOpen,
	onClose
}: FormPreviewModalProps) => {
	const methods = useForm<ObservationFormState>({});

	return (
		<FormProvider {...methods}>
			<Dialog
				open={isOpen}
				onOpenChange={(open) => {
					if (!open) onClose();
				}}
			>
				<DialogContent className="w-[90%] max-w-[900px] p-0 overflow-y-auto max-h-[85vh] rounded-xl [&>button:last-child]:hidden">
					<div className="flex justify-end px-3 pt-3">
						<DialogClose className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md border border-border transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
							<X className="h-4 w-4 text-foreground" />
							<span className="sr-only">Close</span>
						</DialogClose>
					</div>
					<div className="px-6 pb-6">
						<div className="flex flex-row bg-primary-lighter p-4 w-full rounded-lg mb-4">
							<span className="text-sm font-medium text-foreground">Note:</span>
							<span className="ml-1 text-sm text-muted-foreground">
								This is a preview of your form.
							</span>
						</div>

						<DialogTitle className="font-heading text-xl font-semibold text-foreground mb-6">
							{formName}
						</DialogTitle>
						<DialogDescription className="sr-only">
							Preview of the observation form questions
						</DialogDescription>

						<div className="flex flex-col gap-4 bg-canvas rounded-lg p-4">
							{questions &&
								questions
									.sort(
										(question1, question2) => question1.order - question2.order
									)
									.map((question) => {
										return (
											<ObservationQuestionLayout
												key={question.id}
												isDisabled={false}
												isLoading={false}
												question={question.question}
												questionNumber={question.order}
												note={question.note}
												observationAnswers={formatAnswers(question.answers)}
												questionType={question.type}
												order={question.order}
												error={false}
												formStatic={true}
												image={question.image}
												showAction={Boolean(question.isActionRequired)}
												showImageUploadButton={true}
											/>
										);
									})}
						</div>
					</div>

					{!hideButtons && (
						<div className="flex flex-row justify-end gap-3 px-6 pb-6">
							<Button
								type="button"
								onClick={onClose}
								variant="outline"
								size="lg"
							>
								Back to Edit
							</Button>

							<Button
								type="button"
								onClick={onPublish}
								variant="default"
								size="lg"
							>
								Publish
							</Button>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</FormProvider>
	);
};

export default FormPreviewModal;
